/**
 * The DSCR workbook's OWN change history (backend 2026-08-25).
 *
 * Every `POST /pricing/dscr_calc/{dealId}` diffs the rows against the stored
 * document and appends to `changeHistory[]`:
 *
 *   { changedBy, changedDateTime,
 *     changes: { rows_added[], rows_removed[],
 *                rows_changed[{ row, fields: { field: { old, new } } }],
 *                programFloor: { old, new } } }
 *
 * No-change saves append nothing, so every entry here is a real edit. The
 * server also enforces the verified-reset invariant (editing amount / haircut
 * on a verified row flips `verified` false) — that flip shows up as one more
 * field in the row diff, which is exactly why the trail is worth rendering:
 * a seeded value → UW's evidenced value is a `rows_changed` entry.
 *
 * Same treatment as the admin ChangeHistoryDrawer (entry rail on the left,
 * detail on the right), but fed from the GET response rather than the
 * `/admin/audit-log` API, and rendered as per-row old→new readouts rather
 * than JSON panes — a field diff reads better as "Amount $2,400 → $2,250".
 *
 * `initialEntry` — the loan's pre-workbook values, captured ONCE on the first
 * save with sources — is the baseline the trail starts from, so it is pinned
 * as the oldest entry ("initial entry vs finalized").
 */
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import { amber, grey } from "@mui/material/colors";
import size from "lodash/size";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { teal } from "../../../../theme/colors";
import { listLoanUnits, rentBasisLabel } from "./constants";

const INITIAL_ID = "__initial_entry__";

const FIELD_LABELS = {
  componentType: "Component",
  label: "Label",
  amount: "Amount",
  countedPercentage: "Counted %",
  verified: "Verified",
  notes: "Notes",
  loanDocumentIds: "Documents",
  unitId: "Unit",
  rentBasis: "Rent basis",
};

// Timestamps are written `datetime.now()` on a UTC-clocked server → Eastern.
export const historyWhen = (ts) => (ts ? formatDateTime(ts, "datetime") || String(ts) : "—");

const fmtDscr = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}x`;

/** One field's value, formatted for its kind — documents resolve to names. */
const formatFieldValue = (field, value, ctx) => {
  if (value == null || value === "") return "—";
  switch (field) {
    case "amount":
      return formatDollar(value, "—");
    case "countedPercentage":
      return `${Number(value)}%`;
    case "verified":
      return value ? "Yes" : "No";
    case "rentBasis":
      return rentBasisLabel(value) || String(value);
    case "unitId": {
      const u = ctx.unitsById.get(String(value));
      return u ? `Unit ${u.unitNumber}` : `…${String(value).slice(-6)}`;
    }
    case "loanDocumentIds": {
      const ids = Array.isArray(value) ? value : [];
      if (!ids.length) return "none";
      return ids
        .map((id) => ctx.docsById.get(String(id)) || `…${String(id).slice(-6)}`)
        .join(", ");
    }
    default:
      return String(value);
  }
};

export const entrySummary = (changes) => {
  const parts = [];
  if (size(changes?.rows_changed)) parts.push(`${size(changes.rows_changed)} changed`);
  if (size(changes?.rows_added)) parts.push(`${size(changes.rows_added)} added`);
  if (size(changes?.rows_removed)) parts.push(`${size(changes.rows_removed)} removed`);
  if (changes?.programFloor) parts.push("floor");
  return parts.join(" · ") || "no changes";
};

/* ─────────────────────────────── readouts ───────────────────────────────── */

const SectionLabel = ({ children }) => (
  <Txt
    sx={{
      display: "block",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      color: grey[500],
      mb: 0.75,
    }}
  >
    {children}
  </Txt>
);

/**
 * A field-level diff for one row — "Amount  $2,400 → $2,250". This is a diff
 * readout (label / before / arrow / after), not a data grid, so it's a Flx
 * stack rather than AG Grid.
 */
const FieldDiff = ({ field, change, ctx }) => (
  <Flx ac g={1} wrap sx={{ py: 0.5 }}>
    <Txt sx={{ fontSize: 12, color: grey[600], minWidth: 88 }}>
      {FIELD_LABELS[field] || field}
    </Txt>
    <Txt
      sx={{
        fontSize: 12.5,
        color: grey[600],
        textDecoration: "line-through",
        textDecorationColor: grey[400],
      }}
    >
      {formatFieldValue(field, change?.old, ctx)}
    </Txt>
    <ArrowForwardRounded sx={{ fontSize: 14, color: grey[400] }} />
    <Txt
      sx={{
        fontSize: 12.5,
        fontWeight: 600,
        color:
          field === "verified" && change?.new === false ? amber[900] : "#0f172a",
      }}
    >
      {formatFieldValue(field, change?.new, ctx)}
    </Txt>
    {field === "verified" && change?.old === true && change?.new === false ? (
      <Chip
        size="small"
        label="reset by edit"
        sx={{
          height: 18,
          fontSize: 10,
          fontWeight: 700,
          bgcolor: amber[50],
          color: amber[900],
          border: `1px solid ${amber[200]}`,
        }}
      />
    ) : null}
  </Flx>
);

const RowCard = ({ title, tone, children }) => (
  <Box
    sx={{
      border: "1px solid #e5e7eb",
      borderLeft: `3px solid ${tone}`,
      borderRadius: "8px",
      px: 1.75,
      py: 1.25,
      bgcolor: "#fff",
    }}
  >
    <Txt sx={{ display: "block", fontSize: 13, fontWeight: 600, mb: 0.25 }}>
      {title}
    </Txt>
    {children}
  </Box>
);

/** An added / removed row: its snapshot at the time of the save. */
const SnapshotRow = ({ snap, ctx }) => (
  <Flx column g={0}>
    <Txt sx={{ fontSize: 12, color: grey[600] }}>
      {snap?.componentType || "—"} · {formatDollar(snap?.amount, "—")} ·{" "}
      {snap?.countedPercentage == null ? "100" : Number(snap.countedPercentage)}%
      counted · {snap?.verified ? "verified" : "not verified"}
    </Txt>
    <Txt sx={{ fontSize: 12, color: grey[600] }}>
      Documents: {formatFieldValue("loanDocumentIds", snap?.loanDocumentIds, ctx)}
    </Txt>
    {snap?.notes ? (
      <Txt sx={{ fontSize: 12, color: grey[600] }}>Notes: {snap.notes}</Txt>
    ) : null}
  </Flx>
);

export const EntryDetail = ({ entry, ctx }) => {
  const changes = entry?.changes || {};
  return (
    <Flx column g={2.5}>
      <Box>
        <Txt sx={{ display: "block", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          {entrySummary(changes)}
        </Txt>
        <Txt sx={{ fontSize: 12, color: grey[600] }}>
          by {entry?.changedBy || "—"} · {historyWhen(entry?.changedDateTime)}
        </Txt>
      </Box>

      {changes.programFloor ? (
        <Box>
          <SectionLabel>Program floor</SectionLabel>
          <RowCard title="Program floor" tone={teal[500]}>
            <Flx ac g={1}>
              <Txt sx={{ fontSize: 12.5, color: grey[600], textDecoration: "line-through" }}>
                {fmtDscr(changes.programFloor.old)}
              </Txt>
              <ArrowForwardRounded sx={{ fontSize: 14, color: grey[400] }} />
              <Txt sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {fmtDscr(changes.programFloor.new)}
              </Txt>
            </Flx>
          </RowCard>
        </Box>
      ) : null}

      {size(changes.rows_changed) ? (
        <Box>
          <SectionLabel>Changed rows ({size(changes.rows_changed)})</SectionLabel>
          <Flx column g={1}>
            {changes.rows_changed.map((r, i) => (
              <RowCard key={`${r?.row}-${i}`} title={r?.row || "Row"} tone={teal[500]}>
                {Object.entries(r?.fields || {}).map(([field, change]) => (
                  <FieldDiff key={field} field={field} change={change} ctx={ctx} />
                ))}
              </RowCard>
            ))}
          </Flx>
        </Box>
      ) : null}

      {size(changes.rows_added) ? (
        <Box>
          <SectionLabel>Added rows ({size(changes.rows_added)})</SectionLabel>
          <Flx column g={1}>
            {changes.rows_added.map((snap, i) => (
              <RowCard
                key={`${snap?.label}-${i}`}
                title={snap?.label || snap?.componentType || "Row"}
                tone="#047857"
              >
                <SnapshotRow snap={snap} ctx={ctx} />
              </RowCard>
            ))}
          </Flx>
        </Box>
      ) : null}

      {size(changes.rows_removed) ? (
        <Box>
          <SectionLabel>Removed rows ({size(changes.rows_removed)})</SectionLabel>
          <Flx column g={1}>
            {changes.rows_removed.map((snap, i) => (
              <RowCard
                key={`${snap?.label}-${i}`}
                title={snap?.label || snap?.componentType || "Row"}
                tone="#dc2626"
              >
                <SnapshotRow snap={snap} ctx={ctx} />
              </RowCard>
            ))}
          </Flx>
        </Box>
      ) : null}
    </Flx>
  );
};

/** The baseline: what the loan said BEFORE the workbook's first save. */
const InitialEntryDetail = ({ initialEntry }) => (
  <Flx column g={2.5}>
    <Box>
      <Txt sx={{ display: "block", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
        Initial entry
      </Txt>
      <Txt sx={{ fontSize: 12, color: grey[600] }}>
        The loan's values at creation, captured once on the workbook's first save ·{" "}
        {historyWhen(initialEntry?.capturedAt)}
      </Txt>
    </Box>
    <RowCard title="Pre-workbook loan values" tone={grey[400]}>
      <Flx column g={0.5}>
        {[
          ["DSCR (as entered)", fmtDscr(initialEntry?.dscr)],
          ["Total in-place rents", formatDollar(initialEntry?.totalInPlaceRents, "—")],
          ["Total market rents", formatDollar(initialEntry?.totalMarketRents, "—")],
        ].map(([label, value]) => (
          <Flx key={label} ac jb g={2}>
            <Txt sx={{ fontSize: 12, color: grey[600] }}>{label}</Txt>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600 }}>{value}</Txt>
          </Flx>
        ))}
      </Flx>
    </RowCard>
    <Txt sx={{ fontSize: 12, color: grey[600], lineHeight: 1.5 }}>
      From the first save onward the doc-sourced workbook values write to the
      loan (creation entry &lt; doc-sourced). Every later entry in this trail is
      a step from this baseline toward the finalized, verified DSCR.
    </Txt>
  </Flx>
);

/* ─────────────────────────────────── drawer ─────────────────────────────── */

const DscrChangeHistoryDrawer = ({ open, onClose, history, initialEntry, loan }) => {
  // Newest first; the server appends in save order.
  const entries = useMemo(
    () =>
      (Array.isArray(history) ? history : [])
        .map((e, i) => ({ ...e, __id: `${e?.changedDateTime || ""}-${i}` }))
        .reverse(),
    [history],
  );
  // Resolvers for ids in the diff: document ids → names, unit ids → numbers.
  const ctx = useMemo(
    () => ({
      docsById: new Map(
        (loan?.loanDocuments || []).map((d) => [
          String(d._id),
          d.file_display_name || d.fileName || d.docType || String(d._id),
        ]),
      ),
      unitsById: new Map(listLoanUnits(loan).map((u) => [String(u.unitId), u])),
    }),
    [loan],
  );

  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (selectedId && (selectedId === INITIAL_ID || entries.some((e) => e.__id === selectedId)))
      return;
    setSelectedId(entries[0]?.__id || (initialEntry ? INITIAL_ID : null));
  }, [open, entries, initialEntry, selectedId]);

  const selected = entries.find((e) => e.__id === selectedId) || null;
  const hasAnything = entries.length > 0 || !!initialEntry;

  const railItemSx = (sel) => ({
    px: 2,
    py: 1.25,
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    borderLeft: "3px solid",
    borderLeftColor: sel ? teal[700] : "transparent",
    bgcolor: sel ? "#f1f6f9" : "transparent",
    "&:hover": { bgcolor: sel ? "#f1f6f9" : "#f8fafc" },
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", md: 860 } } }}
    >
      <Flx
        ac
        jb
        sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #d8dee4", flex: "0 0 auto" }}
      >
        <Flx ac g={1.25}>
          <HistoryOutlined sx={{ fontSize: 20, color: grey[600] }} />
          <Box>
            <Txt sx={{ display: "block", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              DSCR change history
            </Txt>
            <Txt sx={{ fontSize: 12, color: grey[500] }}>
              {entries.length} save{entries.length === 1 ? "" : "s"} with changes
              {initialEntry ? " · initial entry captured" : ""}
            </Txt>
          </Box>
        </Flx>
        <IconButton size="small" onClick={onClose}>
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Flx>

      {!hasAnything ? (
        <Box sx={{ p: 3 }}>
          <EmptyStatePlaceholder
            icon={<HistoryOutlined />}
            title="No changes recorded yet"
            description="Edit a row, attach a document or set the program floor — every save that changes something lands here with who, when and the before → after."
          />
        </Box>
      ) : (
        <Flx sx={{ flex: 1, minHeight: 0 }}>
          {/* Entry rail — a single-column navigation list, not a data table. */}
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              borderRight: "1px solid #eef2f5",
              overflowY: "auto",
            }}
          >
            {entries.map((e) => {
              const sel = e.__id === selectedId;
              return (
                <Box key={e.__id} onClick={() => setSelectedId(e.__id)} sx={railItemSx(sel)}>
                  <Flx ac jb g={1} sx={{ mb: 0.25 }}>
                    <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
                      {entrySummary(e.changes)}
                    </Txt>
                  </Flx>
                  <Txt
                    sx={{
                      display: "block",
                      fontSize: 11,
                      color: grey[600],
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {e.changedBy || "—"}
                  </Txt>
                  <Txt sx={{ fontSize: 10.5, color: grey[500] }}>{historyWhen(e.changedDateTime)}</Txt>
                </Box>
              );
            })}
            {initialEntry ? (
              <Box
                onClick={() => setSelectedId(INITIAL_ID)}
                sx={railItemSx(selectedId === INITIAL_ID)}
              >
                <Flx ac g={0.75} sx={{ mb: 0.25 }}>
                  <Chip
                    size="small"
                    label="baseline"
                    sx={{
                      height: 17,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: grey[100],
                      color: grey[700],
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                  <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
                    Initial entry
                  </Txt>
                </Flx>
                <Txt sx={{ fontSize: 10.5, color: grey[500] }}>
                  {historyWhen(initialEntry.capturedAt)}
                </Txt>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: 2.5, bgcolor: "#fafbfc" }}>
            {selectedId === INITIAL_ID && initialEntry ? (
              <InitialEntryDetail initialEntry={initialEntry} />
            ) : selected ? (
              <EntryDetail entry={selected} ctx={ctx} />
            ) : (
              <Txt sx={{ fontSize: 12.5, color: grey[500] }}>
                Select a save on the left to see what changed.
              </Txt>
            )}
          </Box>
        </Flx>
      )}
    </Drawer>
  );
};

DscrChangeHistoryDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  history: PropTypes.array,
  initialEntry: PropTypes.object,
  loan: PropTypes.object,
};

export default DscrChangeHistoryDrawer;
